import React, { useState, useEffect } from "react";
import {
    Grid,
    Typography,
    Box,
    TextField,
    InputAdornment,
    List,
    ListItem,
    IconButton,
    ListItemText,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Paper,
    Button,
    Checkbox,
} from "@mui/material";
import {
    Add,
    AddCircleOutline,
    AddOutlined,
    Search,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import axios from "../../../../utils/axiosInstance";
import GeneralModalV1 from "../../../UI/atoms/Popup/GeneralModalV1";
import DeleteModal from "../../../UI/atoms/Popup/DeleteModal";
import { BOOST_ACTION } from "../../LandingPage/constants";
import {
    ACTION,
    PERMISSIONS,
    usePermissions,
} from "../../../../utils/permissions";

const BoostSearch = () => {
    const [searchResults, setSearchResults] = useState<any[]>([]);
    const [boostedTitles, setBoostedTitles] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [boostTitleLoading, setBoostTitleLoading] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState("");
    const { enqueueSnackbar } = useSnackbar();
    const [totalCount, setTotalCount] = useState(0);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedItems, setSelectedItems] = useState<any[]>([]);
    const [addBoostModalOpen, setAddBoostModalOpen] = useState(false);
    const [selectedAsset, setSelectedAsset] = useState<any>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { hasPermission } = usePermissions();

    useEffect(() => {
        getBoostedTitles();
    }, []);

    const searchTitles = async (
        searchString: string,
        skip: number = 0,
        limit: number = 200
    ) => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/search/title-search`,
                {
                    params: { searchString, skip, limit },
                }
            );
            setSearchResults(response.data?.data?.list || []);
            setTotalCount(response.data?.data?.count || 0);
        } catch (error: any) {
            console.log("Error in searching titles", error.response);
            enqueueSnackbar(
                error.response?.data?.message || "Failed to search titles",
                { variant: "error" }
            );
        } finally {
            setLoading(false);
        }
    };

    const getBoostedTitles = async () => {
        try {
            setBoostTitleLoading(true);
            const response = await axios.get(
                `${process.env.BASE_URL}api/v1/cms/search/boost-rules`
            );
            setBoostedTitles(response.data?.data?.boostTitles || []);
        } catch (error: any) {
            console.log("Error in getting boosted titles", error.response);
            enqueueSnackbar(
                error.response?.data?.message ||
                    "Failed to fetch boosted titles",
                { variant: "error" }
            );
        } finally {
            setBoostTitleLoading(false);
        }
    };

    const addBoostResultToTray = (assetInfo: any) => {
        if (boostedTitles.length >= 20) {
            enqueueSnackbar("You can add only 20 titles", {
                variant: "warning",
            });
            return;
        }

        if (boostedTitles.some((title) => title.assetId === assetInfo.id)) {
            enqueueSnackbar("Asset already present in boosted titles", {
                variant: "warning",
            });
        } else {
            const title = {
                assetName: assetInfo.name,
                assetId: assetInfo.id,
            };
            setSelectedAsset(title);
            setAddBoostModalOpen(true);
        }
    };

    const updateBoostedTitle = async (titles: any, action: string) => {
        try {
            // setLoading(true);
            const response = await axios.post(
                `${process.env.BASE_URL}api/v1/cms/search/update-rules`,
                { titles }
            );
            const message =
                action === BOOST_ACTION.BOOST
                    ? "Title added successfully"
                    : "Title removed successfully";
            enqueueSnackbar(message, { variant: "success" });
            // getBoostedTitles(); // Refresh the list after update
        } catch (error: any) {
            console.log("Error in updating boosted title", error.response);
            enqueueSnackbar(
                error.response?.data?.message || "Failed to boost title",
                { variant: "error" }
            );
        } finally {
            // setLoading(false);
        }
    };

    const handleSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            searchTitles(searchKeyword);
        }
    };

    const handleSelectRow = (event: any, assetInfo: any) => {
        setSelectedItems((prevSelected) => {
            const existingItem = prevSelected.find(
                (item) => item.assetId === assetInfo?.assetId
            );
            if (existingItem) {
                return prevSelected.filter(
                    (item) => item.assetId !== assetInfo?.assetId
                );
            } else {
                return [
                    ...prevSelected,
                    {
                        assetName: assetInfo?.assetName,
                        assetId: assetInfo?.assetId,
                    },
                ];
            }
        });
    };

    const closeBoostModal = () => {
        setSelectedAsset(null);
        setAddBoostModalOpen(false);
    };

    const confirmBoost = () => {
        const boostResuls = [selectedAsset, ...boostedTitles];
        console.log("boostResuls", boostResuls);
        updateBoostedTitle(boostResuls, BOOST_ACTION.BOOST);
        setBoostedTitles(boostResuls);
        setAddBoostModalOpen(false);
    };

    const closeDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const handleDeleteConfirm = () => {
        setIsDeleteModalOpen(false);

        const updatedBoostedTitles = boostedTitles.filter(
            (title) =>
                !selectedItems.some((item) => item.assetId === title.assetId)
        );
        setBoostedTitles(updatedBoostedTitles);
        setSelectedItems([]);
        setIsEditing(false);
        updateBoostedTitle(updatedBoostedTitles, BOOST_ACTION.DELETE);
    };

    return (
        <Box className="config-heading">
            <Grid container spacing={2}>
                <Grid item xs={7}>
                    <Typography variant="h5">Search Optimization</Typography>
                    <Typography variant="body1">
                        Elasticsearch is set to boost titles that Originals, New
                        Releases, and Recently Updated. The Search Optimization
                        tool below can be used to boost specific titles when
                        there is a match with the user's search criteria.
                    </Typography>
                </Grid>
                <Grid item xs={6}>
                    <Box className="layout-box">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Search for titles"
                                value={searchKeyword}
                                onChange={(e) =>
                                    setSearchKeyword(e.target.value)
                                }
                                onKeyPress={handleSearch}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Search />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Box>
                        {loading ? (
                            <CircularProgress size={20} color="primary" />
                        ) : (
                            <Box
                                component={Paper}
                                sx={{
                                    height: "calc(100vh - 400px)",
                                    overflow: "auto",
                                }}
                            >
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="45%">
                                                <h3>Name</h3>
                                            </TableCell>
                                            <TableCell width="45%">
                                                <h3>Asset ID</h3>
                                            </TableCell>
                                            <TableCell width="10%"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {searchResults.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell>
                                                    {item.name}
                                                </TableCell>
                                                <TableCell>{item.id}</TableCell>
                                                {hasPermission(
                                                    PERMISSIONS.curation,
                                                    ACTION.EDIT
                                                ) && (
                                                    <TableCell>
                                                        <IconButton
                                                            edge="end"
                                                            aria-label="add"
                                                            onClick={() =>
                                                                addBoostResultToTray(
                                                                    item
                                                                )
                                                            }
                                                        >
                                                            <AddCircleOutline />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </Box>
                </Grid>
                {/* BOOSTED TITLES STARTS HERE */}
                <Grid item xs={6}>
                    {boostTitleLoading ? (
                        <CircularProgress size={20} color="primary" />
                    ) : (
                        <Box component={Paper} className="layout-box">
                            {hasPermission(
                                PERMISSIONS.curation,
                                ACTION.EDIT
                            ) && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 1,
                                    }}
                                >
                                    <Typography variant="h6" sx={{ ml: 1 }}>
                                        Boosted Titles
                                    </Typography>
                                    {!isEditing ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </Button>
                                    ) : (
                                        <Box>
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                onClick={() =>
                                                    setIsDeleteModalOpen(true)
                                                }
                                                sx={{ mr: 2 }}
                                            >
                                                Delete
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={() =>
                                                    setIsEditing(false)
                                                }
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <Box
                                component={Paper}
                                sx={{
                                    height: "calc(100vh - 400px)",
                                    overflow: "auto",
                                }}
                            >
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox"></TableCell>
                                            <TableCell width="45%">
                                                <h3>Name</h3>
                                            </TableCell>
                                            <TableCell width="45%">
                                                <h3>Asset ID</h3>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boostedTitles.map((item) => (
                                            <TableRow key={item.assetId}>
                                                <TableCell padding="checkbox">
                                                    {isEditing && (
                                                        <Checkbox
                                                            checked={selectedItems.some(
                                                                (
                                                                    selectedItem
                                                                ) =>
                                                                    selectedItem.assetId ===
                                                                    item.assetId
                                                            )}
                                                            onChange={(event) =>
                                                                handleSelectRow(
                                                                    event,
                                                                    item
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {item.assetName}
                                                </TableCell>
                                                <TableCell>
                                                    {item.assetId}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Box>
                    )}
                </Grid>
            </Grid>
            <GeneralModalV1
                title="Confirmation?"
                isOpen={addBoostModalOpen}
                handleClose={closeBoostModal}
                handleConfirm={confirmBoost}
            >
                <p>
                    {" "}
                    Are you sure you want to boost {selectedAsset?.assetName} in
                    search?
                </p>
            </GeneralModalV1>
            <DeleteModal
                title="confirmation"
                isOpen={isDeleteModalOpen}
                handleClose={closeDeleteModal}
                handleConfirm={handleDeleteConfirm}
            >
                <p>
                    Are you sure you want to remove selected titles from the
                    list?
                </p>
            </DeleteModal>
        </Box>
    );
};

export default BoostSearch;
